import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined, LinkOutlined, SoundOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { ScheduleToggle } from '@/components/ScheduleToggle';
import { CostTag } from '../../ai/ui/CostTag';
import {
  movieReviewApi,
  movieReviewChannelApi,
  type MovieReview,
  type MovieReviewStatus,
} from '../moviereview.api';

/** Statuses where the pipeline is still working and the list should poll. */
const IN_PROGRESS: MovieReviewStatus[] = ['scripting', 'queued', 'assembling'];

const STATUS_COLOR: Record<MovieReviewStatus, string> = {
  scripting: 'processing',
  awaiting_footage: 'warning',
  queued: 'processing',
  assembling: 'processing',
  ready: 'success',
  failed: 'error',
};

const STATUS_LABEL: Record<MovieReviewStatus, string> = {
  scripting: 'Choosing a film',
  awaiting_footage: 'Needs footage',
  queued: 'Queued',
  assembling: 'Rendering',
  ready: 'Ready',
  failed: 'Failed',
};

/** Mirrors MAX_SOURCES on the server, so the UI stops before the API refuses. */
const MAX_CLIPS = 6;

const secondsLabel = (s: number): string =>
  s > 0 ? `${String(Math.floor(s / 60))}:${String(Math.round(s % 60)).padStart(2, '0')}` : '';

interface Props {
  orgId: string;
  projectId: string;
  canEdit: boolean;
}

export function MovieReviewTab({ orgId, projectId, canEdit }: Props): React.ReactElement {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['movie-reviews', orgId, projectId] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => movieReviewApi.list(orgId, projectId),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => IN_PROGRESS.includes(r.status)) ? 4000 : false,
  });
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: key });

  const create = useMutation({
    mutationFn: () => movieReviewApi.create(orgId, projectId),
    onSuccess: () => {
      invalidate();
      message.success("Picking a film — you'll be asked for footage shortly");
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to start a review'),
  });

  // Channel settings: when finished Reels should go out. Kept in local state so
  // the switch and time move together and save as one deliberate action —
  // toggling auto-publish without a time would otherwise be a half-configured
  // channel that publishes at whatever the default happened to be.
  const channelKey = ['movie-review-channel', orgId, projectId] as const;
  const channelQuery = useQuery({
    queryKey: channelKey,
    queryFn: () => movieReviewChannelApi.get(orgId, projectId),
  });
  const [autoPublish, setAutoPublish] = useState<boolean | null>(null);
  const [publishTime, setPublishTime] = useState<string | null>(null);
  const channel = channelQuery.data;
  // Server values until the operator touches a control; their edit wins after.
  const autoPublishValue = autoPublish ?? channel?.autoPublish ?? false;
  const publishTimeValue = publishTime ?? channel?.publishTime ?? '18:00';

  const saveChannel = useMutation({
    mutationFn: () =>
      movieReviewChannelApi.update(orgId, projectId, {
        autoPublish: autoPublishValue,
        publishTime: publishTimeValue,
      }),
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: channelKey });
      setAutoPublish(null);
      setPublishTime(null);
      message.success(
        c.autoPublish ? `Auto-publish on, daily at ${c.publishTime}` : 'Auto-publish off',
      );
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not save'),
  });

  // Progress per review, so uploading one card never blanks out the others.
  // The transfer is the only synchronous part of this flow — the render is a
  // background agent — so showing real progress is what makes it feel unblocked.
  const [progress, setProgress] = useState<Record<string, number>>({});
  const setPct = (id: string, pct: number | null): void =>
    setProgress((prev) => {
      if (pct === null) {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: pct };
    });

  const upload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      movieReviewApi.uploadFootage(orgId, projectId, id, file, (pct) => setPct(id, pct)),
    onSuccess: (data, vars) => {
      setPct(vars.id, null);
      invalidate();
      message.success(
        `${String(data.sources.length)} clip${data.sources.length === 1 ? '' : 's'} attached`,
      );
    },
    onError: (e, vars) => {
      setPct(vars.id, null);
      message.error(e instanceof Error ? e.message : 'Upload failed');
    },
  });

  // Pasted links, per card. The server pulls each one down through the render
  // service, which can take a while — hence its own pending state rather than
  // sharing the upload progress bar, which measures a byte transfer from here.
  const [linkDraft, setLinkDraft] = useState<Record<string, string>>({});

  const addLinks = useMutation({
    mutationFn: ({ id, links }: { id: string; links: string }) =>
      movieReviewApi.addLinks(orgId, projectId, id, links),
    onSuccess: (data, vars) => {
      setLinkDraft((prev) => {
        const { [vars.id]: _drop, ...rest } = prev;
        return rest;
      });
      invalidate();
      // Partial success is the common case with a pasted batch, so both halves
      // are reported rather than only the happy one.
      if (data.failures.length) {
        message.warning(
          `Could not fetch ${String(data.failures.length)}: ${data.failures
            .map((f) => `${f.link} (${f.reason})`)
            .join('; ')}`,
          8,
        );
      }
      if (data.review.sources.length) {
        message.success(
          `${String(data.review.sources.length)} clip${
            data.review.sources.length === 1 ? '' : 's'
          } attached`,
        );
      }
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not fetch those links'),
  });

  const removeSource = useMutation({
    mutationFn: ({ id, index }: { id: string; index: number }) =>
      movieReviewApi.removeSource(orgId, projectId, id, index),
    onSuccess: invalidate,
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not remove that clip'),
  });

  const build = useMutation({
    mutationFn: (id: string) => movieReviewApi.render(orgId, projectId, id),
    onSuccess: () => {
      invalidate();
      message.success('Building the Reel — analysing your clips for the best moments');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not start the build'),
  });

  const uploadReviewMusic = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      movieReviewApi.uploadMusic(orgId, projectId, id, file),
    onSuccess: () => {
      invalidate();
      message.success('Music attached to this review');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not upload that track'),
  });

  const clearReviewMusic = useMutation({
    mutationFn: (id: string) => movieReviewApi.clearMusic(orgId, projectId, id),
    onSuccess: () => {
      invalidate();
      message.success("Back to the channel's music");
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not remove that track'),
  });

  const uploadChannelMusic = useMutation({
    mutationFn: (file: File) => movieReviewChannelApi.uploadMusic(orgId, projectId, file),
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: channelKey });
      message.success(`Every Reel will now use ${c.musicName}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not upload that track'),
  });

  const clearChannelMusic = useMutation({
    mutationFn: () => movieReviewChannelApi.clearMusic(orgId, projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: channelKey });
      message.success('Back to the generated theme');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not remove that track'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => movieReviewApi.publish(orgId, projectId, id),
    onSuccess: () => {
      invalidate();
      message.success('Published to Facebook Reels');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Publish failed'),
  });

  // The picked slot lives per-card: two reviews can be scheduled to different
  // times without one overwriting the other's picker.
  const [slot, setSlot] = useState<Record<string, Dayjs | null>>({});

  const schedule = useMutation({
    mutationFn: ({ id, at }: { id: string; at: string }) =>
      movieReviewApi.schedule(orgId, projectId, id, at),
    onSuccess: (r) => {
      invalidate();
      message.success(`Scheduled for ${dayjs(r.publishAt).format('D MMM, HH:mm')}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not schedule'),
  });

  const cancelSchedule = useMutation({
    mutationFn: (id: string) => movieReviewApi.cancelSchedule(orgId, projectId, id),
    onSuccess: () => {
      invalidate();
      message.success('Scheduled publish cancelled');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not cancel'),
  });

  // Per-card edit buffer, so typing in one review's synopsis never touches
  // another's, and an unsaved edit survives the list's polling refetch.
  const [draftDescription, setDraftDescription] = useState<Record<string, string>>({});

  const saveDescription = useMutation({
    mutationFn: ({ id, description }: { id: string; description: string }) =>
      movieReviewApi.updateDescription(orgId, projectId, id, description),
    onSuccess: (_r, vars) => {
      // Drop the buffer so the card falls back to the saved value — leaving it
      // would show a stale edit as though it were still unsaved.
      setDraftDescription((prev) => {
        const { [vars.id]: _drop, ...rest } = prev;
        return rest;
      });
      invalidate();
      message.success('Synopsis updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Could not save the synopsis'),
  });

  const retry = useMutation({
    mutationFn: (id: string) => movieReviewApi.retry(orgId, projectId, id),
    onSuccess: invalidate,
    onError: (e) => message.error(e instanceof Error ? e.message : 'Retry failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => movieReviewApi.remove(orgId, projectId, id),
    onSuccess: invalidate,
    onError: (e) => message.error(e instanceof Error ? e.message : 'Delete failed'),
  });

  const reviews = query.data ?? [];

  const renderReview = (r: MovieReview): React.ReactElement => {
    // Narrowed once so the Progress props stay non-optional under
    // exactOptionalPropertyTypes.
    const uploading = progress[r.id];
    return (
      <Card
        key={r.id}
        size="small"
        title={
          <Space wrap>
            <span>{r.title || 'Choosing a film…'}</span>
            {r.year ? <Tag>{r.year}</Tag> : null}
            <Tag color={STATUS_COLOR[r.status]}>
              {/* "Needs footage" is wrong once clips are attached and the
                  operator simply hasn't pressed Build yet. */}
              {r.status === 'awaiting_footage' && r.sources.length
                ? 'Ready to build'
                : STATUS_LABEL[r.status]}
            </Tag>
            <CostTag cost={r.cost} />
            {r.published.length ? <Tag color="blue">Published</Tag> : null}
            {!r.published.length && r.publishAt ? (
              <Tag color="purple">Scheduled {dayjs(r.publishAt).format('D MMM, HH:mm')}</Tag>
            ) : null}
          </Space>
        }
        extra={
          canEdit ? (
            <Popconfirm
              title="Delete this review and its files?"
              onConfirm={() => remove.mutate(r.id)}
            >
              <Button size="small" danger type="text">
                Delete
              </Button>
            </Popconfirm>
          ) : null
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {r.director ? (
            <Typography.Text type="secondary">Directed by {r.director}</Typography.Text>
          ) : null}

          {/* Written by hand, not generated. Nothing could verify a plot
              summary — Wikidata confirms the title, year and director, not the
              story — so it is the operator's line rather than the model's. */}
          {canEdit && !r.published.length ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Synopsis — the first thing readers see on Facebook
              </Typography.Text>
              {r.status === 'ready' && !r.description ? (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  This Reel will publish without a synopsis — the caption starts at the release
                  date.
                </Typography.Text>
              ) : null}
              <Input.TextArea
                rows={3}
                value={draftDescription[r.id] ?? r.description}
                placeholder="Two sentences on what the film is about."
                onChange={(e) =>
                  setDraftDescription((prev) => ({ ...prev, [r.id]: e.target.value }))
                }
              />
              {(draftDescription[r.id] ?? r.description) !== r.description ? (
                <Space size="small">
                  <Button
                    size="small"
                    type="primary"
                    loading={saveDescription.isPending}
                    onClick={() =>
                      saveDescription.mutate({
                        id: r.id,
                        description: draftDescription[r.id] ?? '',
                      })
                    }
                  >
                    Save synopsis
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setDraftDescription((prev) => {
                        const { [r.id]: _drop, ...rest } = prev;
                        return rest;
                      })
                    }
                  >
                    Discard
                  </Button>
                </Space>
              ) : null}
            </Space>
          ) : r.description ? (
            <Typography.Text>{r.description}</Typography.Text>
          ) : null}
          {r.releaseDate ? (
            <Typography.Text type="secondary">
              Released {dayjs(r.releaseDate).format('D MMMM YYYY')}
            </Typography.Text>
          ) : null}

          {r.scores.length ? (
            <Space wrap>
              {r.scores.map((s) => (
                <Tag key={s.label}>
                  {s.label} {s.value}/10
                </Tag>
              ))}
              <Tag color="gold">Overall {r.overall}</Tag>
            </Space>
          ) : null}

          {r.verdict ? <Typography.Text italic>“{r.verdict}”</Typography.Text> : null}

          {r.error ? <Typography.Text type="danger">{r.error}</Typography.Text> : null}

          {/* The manual step: footage can't be sourced automatically, so the
            review pauses here until someone uploads a cut. */}
          {uploading !== undefined ? (
            <Progress
              percent={uploading}
              status={uploading === 100 ? 'success' : 'active'}
              format={() => (uploading === 100 ? 'Saving…' : `Uploading ${String(uploading)}%`)}
            />
          ) : null}

          {/* The clips this Reel is cut from. Several is the point: one trailer
              rarely holds every moment a film is remembered for, and the
              analysis picks across the whole set. */}
          {r.sources.length ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {String(r.sources.length)} of {String(MAX_CLIPS)} clips
              </Typography.Text>
              <Space wrap size={[4, 4]}>
                {r.sources.map((s, i) => (
                  <Tag
                    key={s.url}
                    icon={s.origin ? <LinkOutlined /> : undefined}
                    closable={canEdit && !r.published.length && r.status !== 'assembling'}
                    onClose={(e) => {
                      e.preventDefault();
                      removeSource.mutate({ id: r.id, index: i });
                    }}
                  >
                    {s.label}
                    {secondsLabel(s.durationSec) ? ` · ${secondsLabel(s.durationSec)}` : ''}
                  </Tag>
                ))}
              </Space>
            </Space>
          ) : null}

          {canEdit && !r.published.length && r.sources.length < MAX_CLIPS ? (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {uploading === undefined ? (
                <Upload.Dragger
                  accept="video/*"
                  multiple
                  showUploadList={false}
                  disabled={upload.isPending}
                  beforeUpload={(file) => {
                    upload.mutate({ id: r.id, file: file as unknown as File });
                    return false; // never auto-upload — we send the raw file ourselves
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {r.sources.length ? 'Add more clips' : 'Add your clips'}
                  </p>
                  <p className="ant-upload-hint">
                    Drop in a trailer, a featurette, a scene rip — up to {String(MAX_CLIPS)}. The
                    montage is cut across all of them. Vertical or landscape; landscape is
                    letterboxed, never cropped.
                  </p>
                </Upload.Dragger>
              ) : null}

              {/* For footage that lives on a server rather than on this
                  machine. The API fetches what the page's player would. */}
              <Input.TextArea
                rows={2}
                value={linkDraft[r.id] ?? ''}
                placeholder="…or paste video links, one per line"
                disabled={addLinks.isPending}
                onChange={(e) => setLinkDraft((prev) => ({ ...prev, [r.id]: e.target.value }))}
              />
              <Button
                size="small"
                icon={<LinkOutlined />}
                loading={addLinks.isPending}
                disabled={!(linkDraft[r.id] ?? '').trim()}
                onClick={() => addLinks.mutate({ id: r.id, links: linkDraft[r.id] ?? '' })}
              >
                Fetch from links
              </Button>
            </Space>
          ) : null}

          {/* Music, most specific first: this review's track, else the
              channel's, else the generated theme. */}
          {canEdit && !r.published.length ? (
            <Space wrap size="small">
              <SoundOutlined />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {r.musicUrl ? 'Custom music for this review' : "Using the channel's music"}
              </Typography.Text>
              <Upload
                accept="audio/*"
                showUploadList={false}
                disabled={uploadReviewMusic.isPending}
                beforeUpload={(file) => {
                  uploadReviewMusic.mutate({ id: r.id, file: file as unknown as File });
                  return false;
                }}
              >
                <Button size="small" loading={uploadReviewMusic.isPending}>
                  {r.musicUrl ? 'Replace' : 'Use a different track'}
                </Button>
              </Upload>
              {r.musicUrl ? (
                <Button
                  size="small"
                  type="text"
                  loading={clearReviewMusic.isPending}
                  onClick={() => clearReviewMusic.mutate(r.id)}
                >
                  Remove
                </Button>
              ) : null}
            </Space>
          ) : null}

          {r.videoUrl ? (
            <video
              src={r.videoUrl}
              controls
              style={{ width: '100%', maxWidth: 320, borderRadius: 8 }}
            />
          ) : null}

          {canEdit ? (
            <Space wrap>
              {/* Attaching a clip no longer starts a render — this does. With
                  several clips in play, rendering on each arrival would burn a
                  paid analysis and a full composite on an incomplete set, once
                  per clip, and throw all but the last away. */}
              {r.sources.length > 0 &&
              !r.published.length &&
              r.status !== 'queued' &&
              r.status !== 'assembling' ? (
                <Button
                  type={r.videoUrl ? 'default' : 'primary'}
                  loading={build.isPending}
                  onClick={() => build.mutate(r.id)}
                >
                  {r.videoUrl ? 'Re-cut the Reel' : 'Build the Reel'}
                </Button>
              ) : null}
              {r.status === 'ready' && !r.published.length ? (
                <Button
                  type="primary"
                  loading={publish.isPending}
                  onClick={() => publish.mutate(r.id)}
                >
                  Publish now
                </Button>
              ) : null}
              {r.status === 'ready' && !r.published.length && !r.publishAt ? (
                <>
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    format="D MMM YYYY, HH:mm"
                    placeholder="Pick a time"
                    value={slot[r.id] ?? null}
                    // Past slots would fire the moment they were queued, which
                    // is a surprising way to publish something.
                    disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
                    onChange={(v) => setSlot((prev) => ({ ...prev, [r.id]: v }))}
                  />
                  <Button
                    loading={schedule.isPending}
                    disabled={!slot[r.id]}
                    onClick={() => {
                      const at = slot[r.id];
                      if (at) schedule.mutate({ id: r.id, at: at.toISOString() });
                    }}
                  >
                    Schedule
                  </Button>
                </>
              ) : null}
              {r.publishAt && !r.published.length ? (
                <Button
                  loading={cancelSchedule.isPending}
                  onClick={() => cancelSchedule.mutate(r.id)}
                >
                  Cancel schedule
                </Button>
              ) : null}
              {r.status === 'failed' || (r.status === 'ready' && r.error) ? (
                <Button loading={retry.isPending} onClick={() => retry.mutate(r.id)}>
                  {r.status === 'ready' ? 'Re-run shot selection' : 'Retry'}
                </Button>
              ) : null}
              {r.published[0]?.url ? (
                <Button type="link" href={r.published[0].url} target="_blank" rel="noreferrer">
                  View on Facebook
                </Button>
              ) : null}
            </Space>
          ) : null}
        </Space>
      </Card>
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {canEdit ? (
        <Card size="small" title="New review">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Typography.Text type="secondary">
              The agent picks an acclaimed film this channel hasn&apos;t covered, verifies it
              against Wikidata and scores it out of 10. You then add clips — uploaded or fetched
              from links — and it researches the film&apos;s best-known moments, finds them across
              everything you gave it, and cuts them into a 20-second Reel.
            </Typography.Text>
            <Space wrap align="center">
              <Button type="primary" loading={create.isPending} onClick={() => create.mutate()}>
                Generate a review
              </Button>
              <ScheduleToggle
                orgId={orgId}
                projectId={projectId}
                kind="moviereview_autopilot"
                label="Daily autopilot"
                hint="Each day the agent picks and scores the channel's next film, so there's always one waiting for footage. It pauses while three are already waiting, so a backlog can't build up."
                canEdit={canEdit}
              />
            </Space>

            {/* The generated theme is a floor, not a policy: it exists so a
                channel that has configured nothing still ships a Reel with
                music on it. A channel with its own sound overrides it here. */}
            <Space wrap align="center">
              <SoundOutlined />
              <Typography.Text>
                {channel?.musicUrl
                  ? `Every Reel uses ${channel.musicName || 'your track'}`
                  : 'Every Reel uses the generated theme'}
              </Typography.Text>
              {channel?.musicUrl ? (
                <audio src={channel.musicUrl} controls style={{ height: 32, maxWidth: 240 }} />
              ) : null}
              <Upload
                accept="audio/*"
                showUploadList={false}
                disabled={uploadChannelMusic.isPending}
                beforeUpload={(file) => {
                  uploadChannelMusic.mutate(file as unknown as File);
                  return false;
                }}
              >
                <Button size="small" loading={uploadChannelMusic.isPending}>
                  {channel?.musicUrl ? 'Replace track' : 'Upload your own track'}
                </Button>
              </Upload>
              {channel?.musicUrl ? (
                <Button
                  size="small"
                  type="text"
                  loading={clearChannelMusic.isPending}
                  onClick={() => clearChannelMusic.mutate()}
                >
                  Use the generated theme
                </Button>
              ) : null}
            </Space>

            <Space wrap align="center">
              <Switch
                checked={autoPublishValue}
                loading={channelQuery.isLoading}
                onChange={setAutoPublish}
              />
              <Typography.Text>Auto-publish each Reel when ready</Typography.Text>
              <Typography.Text type="secondary">at</Typography.Text>
              <input
                type="time"
                value={publishTimeValue}
                disabled={!autoPublishValue}
                onChange={(e) => setPublishTime(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9' }}
              />
              <Typography.Text type="secondary">
                {channel?.publishTimezone === 'Africa/Cairo'
                  ? 'Cairo time, daily'
                  : `${channel?.publishTimezone ?? 'Africa/Cairo'}, daily`}
              </Typography.Text>
              <Button
                size="small"
                loading={saveChannel.isPending}
                onClick={() => saveChannel.mutate()}
              >
                Save
              </Button>
            </Space>

            <Typography.Text type="secondary">
              With auto-publish on, a Reel goes to Facebook at your chosen time as soon as it
              finishes rendering — you can still cancel it on the card before then. A Reel that
              finished with a warning is left for you to check rather than published automatically.
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      {query.isLoading ? (
        <Spin />
      ) : reviews.length === 0 ? (
        <Empty description="No reviews yet" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {reviews.map(renderReview)}
        </Space>
      )}
    </Space>
  );
}
